import { Check, ChevronDown, ListChecks, User } from "lucide-react";

import { DatePicker } from "@/components/ui/date-picker";
import { DateQuickPicks } from "@/components/ui/date-quick-picks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectPeople } from "@/hooks/use-project-people";
import { personInitials, personName } from "@/lib/issues";
import { cn } from "@/lib/utils";

/** The fields the card owns. `null` while every one of them is empty. */
export interface RaiseIssueDraft {
  title: string;
  assignedToId: string | null;
  dueDate: string;
}

export const EMPTY_ISSUE_DRAFT: RaiseIssueDraft = {
  title: "",
  assignedToId: null,
  dueDate: "",
};

/**
 * Whether the Title field is shown on the capture-time toggle.
 *
 * Off by design, not dead code: the toggle now sits below Remarks, and
 * `issueTitleOrPlaceholder` already titles the issue from the remarks the
 * user just wrote (falling back to a placeholder), so asking for a title
 * here was asking twice for the same sentence. The field and its wiring are
 * kept intact — flip this to `true` to reinstate it, no other change needed.
 * The draft still carries `title`, so a caller can pre-fill it.
 */
const SHOW_ISSUE_TITLE_FIELD = false;

/**
 * Whether a draft holds anything that makes the record an issue. Filling a
 * field is the promotion; emptying every field is the discard.
 */
function isPromoted(draft: RaiseIssueDraft): boolean {
  return (
    draft.assignedToId !== null || draft.dueDate !== "" || draft.title !== ""
  );
}

interface RaiseIssueToggleProps {
  projectId: string;
  /**
   * Null while idle. Non-null is what makes the record create raise an issue,
   * and the card only hands one back once an assignee or due date is set.
   */
  value: RaiseIssueDraft | null;
  onChange: (next: RaiseIssueDraft | null) => void;
  disabled?: boolean;
}

/**
 * "Promote as issue" — raise an issue in the same act as creating the record.
 *
 * Always expanded, no switch: picking an assignee or a due date is what
 * promotes the record, and clearing both demotes it. The border is the Remarks
 * field's grey while idle and primary once promoted. See
 * [[raise-issue-toggle.spec]].
 */
export function RaiseIssueToggle({
  projectId,
  value,
  onChange,
  disabled = false,
}: RaiseIssueToggleProps) {
  const isOn = value !== null;
  const draft = value ?? EMPTY_ISSUE_DRAFT;
  const { people } = useProjectPeople(projectId);
  const { data: currentUser } = useCurrentUser();
  const selectedPerson =
    people.find((person) => person.id === draft.assignedToId) ?? null;

  // The invisible switch: any filled field hands the host a draft, an all-empty
  // one hands back null, so the submit path reads exactly as it did.
  const patch = (next: Partial<RaiseIssueDraft>) => {
    const merged = { ...draft, ...next };
    onChange(isPromoted(merged) ? merged : null);
  };

  return (
    <div
      role="group"
      aria-labelledby="capture-issue-heading"
      data-promoted={isOn ? "true" : undefined}
      className={cn(
        "overflow-hidden rounded-lg border bg-card transition-colors",
        // Idle matches the Remarks textarea's `border-border` directly above.
        isOn ? "border-primary" : "border-border",
      )}
    >
      <div className="flex items-center gap-3 p-3">
        <ListChecks className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span
          id="capture-issue-heading"
          className="flex-1 text-sm font-medium"
        >
          Promote as issue
        </span>
      </div>

      <div className="flex flex-col gap-3.5 border-t border-border px-3 pb-4 pt-3.5">
        {SHOW_ISSUE_TITLE_FIELD && (
          <section className="flex flex-col gap-2">
            <label
              htmlFor="capture-issue-title"
              className="text-[13px] font-medium"
            >
              Title
            </label>
            <Input
              id="capture-issue-title"
              value={draft.title}
              disabled={disabled}
              onChange={(event) => patch({ title: event.target.value })}
              placeholder="What needs fixing?"
            />
          </section>
        )}

        <section className="flex flex-col gap-2">
          <span className="text-[13px] font-medium">Assign to</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Assign to"
                className="flex h-[46px] w-full items-center justify-between gap-2.5 rounded-lg border border-border bg-card px-3 text-sm"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  {selectedPerson ? (
                    <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-tertiary text-[11px] font-semibold text-tertiary-foreground">
                      {personInitials(selectedPerson)}
                    </span>
                  ) : (
                    <User
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={cn(
                      "truncate",
                      !selectedPerson && "text-muted-foreground",
                    )}
                  >
                    {selectedPerson
                      ? selectedPerson.id === currentUser?.id
                        ? `${personName(selectedPerson)} (me)`
                        : personName(selectedPerson)
                      : "Nobody yet"}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[var(--radix-dropdown-menu-trigger-width)]"
            >
              <DropdownMenuItem onSelect={() => patch({ assignedToId: null })}>
                <span className="flex-1">Nobody yet</span>
                {draft.assignedToId === null && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
              {people.map((person) => (
                <DropdownMenuItem
                  key={person.id}
                  onSelect={() => patch({ assignedToId: person.id })}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tertiary text-[10px] font-semibold text-tertiary-foreground">
                    {personInitials(person)}
                  </span>
                  <span className="flex-1 truncate">
                    {person.id === currentUser?.id
                      ? `${personName(person)} (me)`
                      : personName(person)}
                  </span>
                  {draft.assignedToId === person.id && (
                    <Check className="h-4 w-4" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </section>

        <section className="flex flex-col gap-2">
          <span className="text-[13px] font-medium">Due date</span>
          <DatePicker
            value={draft.dueDate || null}
            onChange={(next) => patch({ dueDate: next ?? "" })}
            aria-label="Due date"
          />
          {/* The same row the raise sheet offers, for the same field — this
              is the surface with the least patience of the two: a snagging
              walk is forty captures, and a calendar between each one is the
              thing that stops people setting a date at all. */}
          <DateQuickPicks
            value={draft.dueDate || null}
            onChange={(next) => patch({ dueDate: next })}
            disabled={disabled}
          />
        </section>
      </div>
    </div>
  );
}
